import { useState, useEffect } from "react";
import { Heart, Mail, Clock, CheckCircle } from "lucide-react";
import { StatsCard } from "@b2b/ui-components";
import { favoritesService, sellerService } from "@b2b/services";

export function Stats() {
  const [data, setData] = useState<{
    favoritesCount: number;
    enquiriesTotal: number;
    pendingCount: number;
    respondedCount: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const [favoritesRes, enquiriesRes, pendingRes, respondedRes] =
          await Promise.all([
            favoritesService.getFavorites(),
            sellerService.getMyStorefrontEnquiries(undefined, 1, 1),
            sellerService.getMyStorefrontEnquiries("PENDING", 1, 1),
            sellerService.getMyStorefrontEnquiries("RESPONDED", 1, 1),
          ]);

        const favoritesList = favoritesRes?.favorites ?? [];
        setData({
          favoritesCount:
            favoritesRes?.total ?? favoritesList.length ?? 0,
          enquiriesTotal: enquiriesRes?.total ?? 0,
          pendingCount: pendingRes?.total ?? 0,
          respondedCount: respondedRes?.total ?? 0,
        });
      } catch (err: unknown) {
        console.error("Error fetching buyer dashboard stats:", err);
        setError(err instanceof Error ? err.message : "Failed to load stats");
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
        {error || "Failed to load overview"}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      <StatsCard
        title="Saved Products"
        value={String(data.favoritesCount)}
        change={
          data.favoritesCount > 0 ? "In your favorites" : "Save products you like"
        }
        changeType="neutral"
        icon={Heart}
        variant="primary"
      />
      <StatsCard
        title="My Enquiries"
        value={String(data.enquiriesTotal)}
        change={
          data.enquiriesTotal > 0
            ? "Sent to sellers"
            : "Enquire on product pages"
        }
        changeType="neutral"
        icon={Mail}
      />
      <StatsCard
        title="Awaiting Response"
        value={String(data.pendingCount)}
        changeType="neutral"
        icon={Clock}
      />
      <StatsCard
        title="Responded"
        value={String(data.respondedCount)}
        changeType="neutral"
        icon={CheckCircle}
      />
    </div>
  );
}
